import {
  channelInfo,
  dealwithChannelChartKey,
} from '@op/components/ChannelLabel';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import { chartAllColors } from '@op/constants';
import { getPercentageInfo } from '@op/utils';
import {
  formatTimeXAxisText,
  getChartPosition,
  getChartsGradientColor,
  getXAxisAxisLabel,
} from '@op/utils/chart';
import { dealWithOthersName } from '@op/utils/languageValue';
import { reviewsFormat, yAxisFormat } from '@op/utils/value';
import React, { useMemo } from 'react';
import { useIntl } from 'umi';

interface IBarAndLineCharts {
  gameInfo: any;
  type: string;
  filter: any;
  data: any;
  height?: number;
  sumLabel?: string;
  seriesType?: string;
  toolTipTitle?: string;
  tooltipRightTitle?: string;
}
export default function BarAndLineCharts(props: IBarAndLineCharts) {
  const {
    type: chartType,
    height = 300,
    filter,
    data,
    sumLabel = 'Mentions',
    seriesType = '',
    toolTipTitle = '',
    tooltipRightTitle = '',
  } = props;

  const { formatMessage } = useIntl();

  const { optionsProps, chartRealType } = useMemo(() => {
    const chartRealType: any = chartType === 'stack_line' ? 'line' : chartType;
    return {
      optionsProps: { type: chartRealType },
      chartRealType,
    };
  }, [chartType]);

  const options = useMemo(() => {
    const { xAxis: xAxisData, legend: legendData, series: seriesData } = data;

    const series = seriesData?.map((val: any, index: number) => {
      const color = chartAllColors[index];
      let areaStyle = undefined;
      let stack = undefined;

      if (['bar', 'stack_line'].includes(chartType)) {
        stack = 'Total';
        if (chartType === 'stack_line') {
          areaStyle = getChartsGradientColor(color, 0, 0.4);
        }
      }

      return {
        ...val,
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 8,
        emphasis: {
          showSymbol: true,
        },
        type: chartRealType,
        itemStyle: {
          color,
        },
        ...(stack && { stack }),
        ...(areaStyle && { areaStyle }),
      };
    });

    const options = {
      grid: {
        top: 20,
        contentLabel: true,
        left: 0,
        right: 5,
      },
      tooltip: {
        trigger: 'axis',
        appendToBody: false,
        confine: false,
        position: (pos: any, params: any, dom: any, rect: any, size: any) =>
          getChartPosition(pos, params, dom, rect, size, -5),
        formatter: (params: any) => {
          let currentDateTotal = 0;
          params?.forEach((item) => (currentDateTotal += item?.value));
          const sumText = formatMessage({ id: sumLabel });
          // if (timelineTotal) {
          //   currentDateTotal =
          //     timelineTotal[params[0]?.name] || currentDateTotal;
          //   sumText = `${sumText} (${formatMessage({
          //     id: 'Removed duplicate reviews',
          //   })})`;
          // }

          return formatChartParamsDate({
            params,
            total: currentDateTotal,
            isSort: true,
            valueFormat: (value: number) => reviewsFormat(value, 2),
            textShowFun: (marker: string, seriesName: string) => {
              let channelIcon = `<img src='${channelInfo(seriesName.toLocaleLowerCase() as any).img
                }'/>`;
              if (seriesType !== 'channel') {
                channelIcon = '';
              }
              return `<span class='echart-tooltip'><span class="chart-common-formatter-marker">${marker}</span>&nbsp;${channelIcon}${dealWithOthersName(
                seriesName,
                seriesType,
              )}</span>`;
            },
            dealwithRecords(records, total) {
              const newRecords: Array<any> = records.map((item) => {
                const share = getPercentageInfo(item.originValue, total, 0);
                return {
                  ...item,
                  value: `${item.value}${!item.originValue ? '' : ` (${share})`
                    }`,
                };
              });

              newRecords.unshift({
                text: sumText,
                value: reviewsFormat(total),
                originValue: total,
                originText: '',
              });
              return newRecords.filter((item) => Boolean(item));
            },
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header:
                    toolTipTitle ||
                    listTimeTitleFunc(params[0]?.name, filter.date_type),
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({ id: tooltipRightTitle }),
                },
              ];

              return [
                {
                  showHeader: true,
                  records,
                  columns,
                },
              ];
            },
          });
        },
      },
      legend: {
        data: legendData,
        formatter: (value: any) =>
          `{${dealwithChannelChartKey(value)}|} {name|${dealWithOthersName(
            value,
            seriesType,
          )}}`,
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filter.date_type),
      ),
      xAxis: {
        type: 'category',
        axisLabel: getXAxisAxisLabel(filter),
        data: xAxisData,
        boundaryGap: ['20%', 0],
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: any) => yAxisFormat(value),
        },
      },
      series,
    };

    return options;
  }, [
    chartRealType,
    chartType,
    data,
    filter,
    formatMessage,
    seriesType,
    sumLabel,
    toolTipTitle,
    tooltipRightTitle,
  ]);

  return (
    <EChartCommon
      options={options}
      optionsProps={optionsProps}
      height={height}
    />
  );
}
